import React, { useState, useEffect, useCallback } from 'react'
import apiClient, { API_ENDPOINTS } from '../../config/api'
import Swal from 'sweetalert2'
import { isSmallShopMode } from '../../utils/smallShopMode'

const ManageBranches = () => {
  const smallShopMode = isSmallShopMode()
  const [branches, setBranches] = useState([])
  const [filteredBranches, setFilteredBranches] = useState([])
  const [warehouses, setWarehouses] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    search: ''
  })

  const applyFilters = useCallback(() => {
    let filtered = [...branches]

    // Search filter (name, address, warehouse_name)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(branch => 
        branch.name?.toLowerCase().includes(searchLower) ||
        branch.address?.toLowerCase().includes(searchLower) ||
        branch.warehouse_name?.toLowerCase().includes(searchLower)
      )
    }

    setFilteredBranches(filtered)
  }, [branches, filters])

  useEffect(() => {
    fetchBranches()
    if (!smallShopMode) {
      fetchWarehouses()
    }
  }, [smallShopMode])

  const fetchWarehouses = async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.WAREHOUSES)
      setWarehouses(response.data.results || response.data || [])
    } catch (error) {
      console.error('Error fetching warehouses:', error)
      setWarehouses([])
    }
  }

  useEffect(() => {
    applyFilters()
  }, [applyFilters])

  const fetchBranches = async () => {
    try {
      setLoading(true)
      const response = await apiClient.get(API_ENDPOINTS.BRANCHES)
      const branchesData = response.data.results || response.data || []
      setBranches(branchesData)
      setFilteredBranches(branchesData)
    } catch (error) {
      console.error('Error fetching branches:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.detail || error.response?.data?.message || 'Failed to fetch branches. Please try again.',
        confirmButtonColor: '#d33'
      })
    } finally {
      setLoading(false)
    }
  }

  const updateBranch = async (branchId, formData) => {
    try {
      // Prepare payload according to backend expectations
      const payload = {
        name: formData.name,
        address: formData.address || '',
      }
      if (!smallShopMode && formData.warehouse) {
        payload.warehouse = parseInt(formData.warehouse, 10)
      }

      // Use PUT method as per backend code
      await apiClient.put(`${API_ENDPOINTS.BRANCHES}${branchId}/`, payload)

      // Refresh branches list
      await fetchBranches()

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Branch updated successfully',
        confirmButtonColor: '#3085d6',
        timer: 2000,
        showConfirmButton: true
      })
    } catch (error) {
      console.error('Error updating branch:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || error.response?.data?.error || error.response?.data?.detail || 'Failed to update branch. Please try again.',
        confirmButtonColor: '#d33'
      })
      throw error
    }
  }

  const editBranch = async (branch) => {
    const currentWarehouseId = branch.warehouse || branch.warehouse_id
    const warehouseFieldHtml = smallShopMode ? '' : `
        <div style="margin-bottom: 12px;">
          <label style="display: block; margin-bottom: 4px; font-weight: 500; color: #2c3e50; font-size: 13px;">Warehouse <span style="color: red;">*</span></label>
          <select id="edit_warehouse" class="swal2-select" required style="width: 100%; max-width: 100%; box-sizing: border-box; padding: 6px 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 14px;">
            <option value="">Select Warehouse</option>
            ${warehouses.map(warehouse => `<option value="${warehouse.id}" ${currentWarehouseId === warehouse.id ? 'selected' : ''}>${warehouse.name || `Warehouse ${warehouse.id}`}</option>`).join('')}
          </select>
        </div>`
    const editFormHtml = `
      <form id="editBranchForm" style="text-align: left; max-width: 100%; box-sizing: border-box; overflow: hidden;">
        ${warehouseFieldHtml}
        <div style="margin-bottom: 12px;">
          <label style="display: block; margin-bottom: 4px; font-weight: 500; color: #2c3e50; font-size: 13px;">Branch Name <span style="color: red;">*</span></label>
          <input type="text" id="edit_name" class="swal2-input" value="${branch.name || ''}" required style="width: 100%; max-width: 100%; box-sizing: border-box; padding: 6px 10px; font-size: 14px;">
        </div>
        <div style="margin-bottom: 12px;">
          <label style="display: block; margin-bottom: 4px; font-weight: 500; color: #2c3e50; font-size: 13px;">Address</label>
          <textarea id="edit_address" class="swal2-textarea" style="width: 100%; max-width: 100%; box-sizing: border-box; padding: 6px 10px; font-size: 14px; min-height: 60px;">${branch.address || ''}</textarea>
        </div>
      </form>
    `

    Swal.fire({
      title: '<span style="color: #2c3e50; font-size: 16px; font-weight: 600;">Edit Branch</span>',
      html: editFormHtml,
      width: '450px',
      padding: '1.5rem',
      showCancelButton: true,
      confirmButtonText: 'Update Branch',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#6c757d',
      customClass: {
        popup: 'swal2-popup-edit',
        htmlContainer: 'swal2-html-container-edit'
      },
      didOpen: () => {
        const popup = document.querySelector('.swal2-popup')
        if (popup) {
          popup.style.maxWidth = '450px'
          popup.style.overflow = 'hidden'
          popup.style.boxSizing = 'border-box'
        }
        
        const htmlContainer = document.querySelector('.swal2-html-container')
        if (htmlContainer) {
          htmlContainer.style.maxWidth = '100%'
          htmlContainer.style.overflow = 'hidden'
          htmlContainer.style.boxSizing = 'border-box'
          htmlContainer.style.padding = '0'
        }

        const form = document.getElementById('editBranchForm')
        if (form) {
          form.style.maxWidth = '100%'
          form.style.overflow = 'hidden'
        }

        const inputs = document.querySelectorAll('#editBranchForm input, #editBranchForm textarea')
        inputs.forEach(input => {
          input.style.maxWidth = '100%'
          input.style.boxSizing = 'border-box'
        })
      },
      preConfirm: async () => {
        const formData = {
          name: document.getElementById('edit_name').value.trim(),
          address: document.getElementById('edit_address').value.trim(),
          warehouse: smallShopMode ? '' : document.getElementById('edit_warehouse')?.value
        }

        if (!formData.name) {
          Swal.showValidationMessage('Branch name is required')
          return false
        }

        if (!smallShopMode && !formData.warehouse) {
          Swal.showValidationMessage('Warehouse is required')
          return false
        }

        return formData
      }
    }).then(async (result) => {
      if (result.isConfirmed && result.value) {
        try {
          await updateBranch(branch.id, result.value)
        } catch (error) {
          // Error already handled in updateBranch
        }
      }
    })
  }


  const handleDelete = async (branchId, branchName) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Delete branch "${branchName}"? This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    })

    if (result.isConfirmed) {
      try {
        await apiClient.delete(`${API_ENDPOINTS.BRANCHES}${branchId}/`)
        
        // Remove branch from state
        setBranches(prevBranches => prevBranches.filter(branch => branch.id !== branchId))
        setFilteredBranches(prevFiltered => prevFiltered.filter(branch => branch.id !== branchId))
        
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Branch has been deleted successfully.',
          confirmButtonColor: '#3085d6',
          timer: 2000,
          showConfirmButton: true
        })
      } catch (error) {
        console.error('Error deleting branch:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.detail || error.response?.data?.message || error.response?.data?.error || 'Failed to delete branch. Please try again.',
          confirmButtonColor: '#d33'
        })
      }
    }
  }

  const navigateToBranchStock = (branch) => {
    const event = new CustomEvent('navigateToScreen', {
      detail: {
        screen: 'branch-stock',
        params: { branch }
      }
    })
    window.dispatchEvent(event)
  }

  const showBranchDetails = (branch) => {
    const detailsHtml = `
      <div style="text-align: left; padding: 10px; background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%); border-radius: 8px; margin-bottom: 10px;">
        <div style="display: flex; align-items: center; margin-bottom: 10px; padding-bottom: 10px; border-bottom: 2px solid rgba(0,0,0,0.1);">
          <div style="width: 40px; height: 40px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 16px; font-weight: bold; margin-right: 10px; box-shadow: 0 2px 8px rgba(0,0,0,0.2);">
            <i class="bi bi-shop"></i>
          </div>
          <div>
            <h3 style="margin: 0; color: #2c3e50; font-weight: 600; font-size: 16px;">${branch.name || 'N/A'}</h3>
            <p style="margin: 2px 0 0 0; color: #7f8c8d; font-size: 12px;">Branch ID: ${branch.id || 'N/A'}</p>
          </div>
        </div>
        
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px;">
          <div style="background: white; padding: 8px; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1);">
            <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Warehouse</div>
            <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${branch.warehouse_name || 'N/A'}</div>
          </div>
          
          <div style="background: white; padding: 8px; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1);">
            <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Warehouse ID</div>
            <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${branch.warehouse || 'N/A'}</div>
          </div>
        </div>
        
        <div style="background: white; padding: 8px; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1);">
          <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Address</div>
          <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${branch.address || 'N/A'}</div>
        </div>
      </div>
    `

    Swal.fire({
      title: '<span style="color: #2c3e50; font-size: 18px; font-weight: 600;">Branch Details</span>',
      html: detailsHtml,
      icon: 'info',
      showCancelButton: true,
      showDenyButton: true,
      showCloseButton: true,
      confirmButtonText: '<i class="bi bi-pencil"></i> Edit Branch',
      denyButtonText: '<i class="bi bi-trash"></i> Delete Branch',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#3085d6',
      denyButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      width: '550px',
      heightAuto: false,
      padding: '1rem',
      customClass: {
        popup: 'swal2-popup-custom'
      },
      footer: '<button id="swal-close-btn" class="btn btn-secondary" style="margin-top: 10px; padding: 8px 16px; border: none; border-radius: 5px;">Close</button>',
      didOpen: () => {
        const confirmButton = Swal.getConfirmButton()
        const denyButton = Swal.getDenyButton()
        const cancelButton = Swal.getCancelButton()
        const closeButton = document.getElementById('swal-close-btn')
        
        if (confirmButton) {
          confirmButton.style.border = 'none'
          confirmButton.style.padding = '10px 20px'
          confirmButton.style.borderRadius = '5px'
          confirmButton.style.fontWeight = '500'
          confirmButton.style.marginRight = '10px'
        }
        
        if (denyButton) {
          denyButton.style.border = 'none'
          denyButton.style.padding = '10px 20px'
          denyButton.style.borderRadius = '5px'
          denyButton.style.fontWeight = '500'
          denyButton.style.marginRight = '10px'
        }

        if (cancelButton) {
          cancelButton.style.border = 'none'
          cancelButton.style.padding = '10px 20px'
          cancelButton.style.borderRadius = '5px'
          cancelButton.style.fontWeight = '500'
          cancelButton.style.marginRight = '10px'
        }

        if (closeButton) {
          closeButton.onclick = () => Swal.close()
        }
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        // Edit branch
        await editBranch(branch)
      } else if (result.isDenied) {
        // Delete branch
        await handleDelete(branch.id, branch.name)
      }
    })
  }

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    })
  }

  const clearFilters = () => {
    setFilters({
      search: ''
    })
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Manage Branches</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">Home</li>
            <li className="breadcrumb-item">Branches</li>
            <li className="breadcrumb-item active">Manage Branches</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="card-title mb-0">
                    <i className="bi bi-shop me-2"></i>
                    Manage Branches
                  </h5>
                  <button 
                    className="btn btn-sm btn-outline-primary"
                    onClick={fetchBranches}
                    disabled={loading}
                  >
                    <i className="bi bi-arrow-clockwise me-1"></i>
                    Refresh
                  </button>
                </div>
                <p className="text-muted">View and manage all branch locations and their information.</p>

                {/* Filters Section */}
                <div className="card mb-3">
                  <div className="card-body">
                    <h6 className="card-title mb-3">
                      <i className="bi bi-funnel me-2"></i>
                      Filters
                    </h6>
                    <div className="row g-3">
                      <div className="col-md-10">
                        <label htmlFor="search" className="form-label">Search</label>
                        <input
                          type="text"
                          className="form-control"
                          id="search"
                          name="search"
                          placeholder="Name, Address, or Warehouse"
                          value={filters.search}
                          onChange={handleFilterChange}
                        />
                      </div>
                      <div className="col-md-2 d-flex align-items-end">
                        <button
                          className="btn btn-outline-secondary w-100"
                          onClick={clearFilters}
                          title="Clear filters"
                        >
                          <i className="bi bi-x-lg me-1"></i>
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Branches Table */}
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading branches...</p>
                  </div>
                ) : filteredBranches.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#ccc' }}></i>
                    <p className="mt-3 text-muted">No branches found</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Branch Name</th>
                          {!smallShopMode && <th>Warehouse</th>}
                          <th>Address</th>
                          <th style={{ width: '140px', textAlign: 'center' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredBranches.map((branch) => (
                          <tr key={branch.id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="avatar-sm bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px', fontSize: '14px' }}>
                                  <i className="bi bi-shop"></i>
                                </div>
                                <div className="fw-semibold">{branch.name}</div>
                              </div>
                            </td>
                            {!smallShopMode && (
                              <td>
                                <span className="badge bg-info">
                                  {branch.warehouse_name || 'N/A'}
                                </span>
                              </td>
                            )}
                            <td>
                              <span className="text-muted">
                                {branch.address || <span className="text-muted">-</span>}
                              </span>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <div className="btn-group gap-2 d-flex justify-content-center">
                                <button
                                  className="btn btn-sm btn-outline-success"
                                  onClick={() => navigateToBranchStock(branch)}
                                  title="View Branch Stock"
                                >
                                  <i className="bi bi-box-seam"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => showBranchDetails(branch)}
                                  title="View Details"
                                >
                                  <i className="bi bi-eye"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Results Count */}
                {!loading && (
                  <div className="mt-3 text-muted">
                    <small>
                      Showing <strong>{filteredBranches.length}</strong> of <strong>{branches.length}</strong> branches
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default ManageBranches

